import type { NodeOrEdge } from '@cognite/sdk';
import { z } from 'zod';

import { deepRemoveDefaults } from '#core/lib/utils/zod.ts';

export const DataModelSchema = z
  .object({
    externalId: z.string(),
    space: z.string().prefault(() => import.meta.env.VITE_APP_DM_DEFAULT_SPACE ?? 'cdf_cdm'),
    version: z.string().prefault(() => import.meta.env.VITE_APP_DM_DEFAULT_VERSION ?? 'v1'),
    instanceSpace: z.string().prefault(() => import.meta.env.VITE_APP_DM_DEFAULT_INSTANCE_SPACE ?? 'cdf_cdm'),
  })
  .transform(({ externalId, space, version, instanceSpace }) => ({
    externalId,
    space: import.meta.env[`VITE_APP_DM_${externalId.toUpperCase()}_SPACE`] ?? space,
    version: import.meta.env[`VITE_APP_DM_VIEW_${externalId.toUpperCase()}_VERSION`] ?? version,
    instanceSpace: import.meta.env[`VITE_APP_DM_${externalId.toUpperCase()}_INSTANCE_SPACE`] ?? instanceSpace,
  }));

export interface DataModelInput extends z.input<typeof DataModelSchema> {}
export interface DataModelOutput extends z.output<typeof DataModelSchema> {}

export const DataModelViewSchema = z
  .object({
    type: z.literal('view').default('view'),
    externalId: z.string(),
    space: z.string().prefault(() => import.meta.env.VITE_APP_DM_VIEW_DEFAULT_SPACE ?? 'cdf_cdm'),
    version: z.string().prefault(() => import.meta.env.VITE_APP_DM_VIEW_DEFAULT_VERSION ?? 'v1'),
  })
  .transform(({ type, externalId, space, version }) => ({
    externalId,
    space: import.meta.env[`VITE_APP_DM_VIEW_${externalId.toUpperCase()}_SPACE`] ?? space,
    type,
    version: import.meta.env[`VITE_APP_DM_VIEW_${externalId.toUpperCase()}_VERSION`] ?? version,
  }));

export interface DataModelViewInput extends z.input<typeof DataModelViewSchema> {}
export interface DataModelViewOutput extends z.output<typeof DataModelViewSchema> {}

export type ParsedNode<
  ViewType extends DataModelViewInput,
  SchemaType extends z.ZodType,
  ExternalIdValueType extends string = ViewType['externalId'] extends string ? ViewType['externalId'] : string,
> = z.util.Prettify<z.output<SchemaType> & { _view: ExternalIdValueType; _dataModel: DataModelOutput }>;

export type DataModelView<
  ViewType extends DataModelViewInput = DataModelViewInput,
  SchemaType extends z.ZodType = z.ZodType,
  ExternalIdValueType extends string = ViewType['externalId'] extends string ? ViewType['externalId'] : string,
  SpaceValueType extends string = ViewType['space'] extends string ? ViewType['space'] : string,
> = z.util.Prettify<{
  type: 'view';
  externalId: ExternalIdValueType;
  space: SpaceValueType;
  version: ViewType['version'] extends string ? ViewType['version'] : `v${string}`;
  dataModel: DataModelOutput;
  schema: z.ZodCodec<z.ZodCustom<NodeOrEdge, NodeOrEdge>, SchemaType>;
  table: `${SpaceValueType}/${ExternalIdValueType}`;
  property: { [SchemaKeyType in keyof Required<z.infer<SchemaType>>]: [string, string, string] };
  parseNode: (node: NodeOrEdge) => ParsedNode<ViewType, SchemaType, ExternalIdValueType>;
}>;

/**
 * Extract column names from a view schema based on their Zod type.
 *
 * Useful for identifying specific column types (e.g., timeseries references)
 * without runtime data inference.
 *
 * @param view - Data model view to extract columns from
 * @returns Array of columns with types and references
 */
export function extractViewColumnTypes<ViewType extends DataModelView>(view?: ViewType) {
  if (!view) {
    return undefined;
  }

  // Capture per-field descriptions from the RAW shape before unwrapping.
  // `.describe()` in the view generator is typically called on wrapped types
  // (ZodOptional/ZodNullable from `.nullish()`), so descriptions live on the
  // outer wrapper. `deepRemoveDefaults` recursively unwraps to the inner
  // primitive, dropping the outer wrapper's description — read descriptions
  // BEFORE that unwrap so they survive.
  // biome-ignore lint/suspicious/noExplicitAny: Zod internal structure access
  const rawShape = ((view.schema.out as any)?.shape ?? {}) as Record<string, unknown>;
  const rawDescriptions = new Map<string, string>();
  for (const [name, rawField] of Object.entries(rawShape)) {
    const description = (rawField as { description?: string } | undefined)?.description;
    if (description) {
      rawDescriptions.set(name, description);
    }
  }

  // biome-ignore lint/suspicious/noExplicitAny: Zod internal structure access
  const viewSchemaUnwrapped = deepRemoveDefaults(view.schema.out as any as z.ZodTypeAny);
  // biome-ignore lint/suspicious/noExplicitAny: Zod internal structure access
  const unwrappedAny = viewSchemaUnwrapped as any as Record<string, unknown>;
  const shape = (unwrappedAny.shape ?? {}) as Record<string, unknown>;
  const propertiesList = new Map(
    Object.entries(shape)
      .filter(([name]) => name !== '__typename')
      .map(([name, fieldSchema]) => {
        // Type guard for Zod schema shape with optional __typename
        const schemaWithShape = fieldSchema as
          | { shape?: { __typename?: { value?: string } }; type?: string }
          | undefined;
        const referencedSchema = schemaWithShape?.shape?.__typename?.value;
        const description = rawDescriptions.get(name);
        return [
          name,
          referencedSchema
            ? { type: 'instanceId', referencedSchema, schema: fieldSchema, description }
            : { type: schemaWithShape?.type ?? 'unknown', schema: fieldSchema, description },
        ];
      }),
  );
  return propertiesList;
}

export const parseView = <
  const ViewType extends DataModelViewInput,
  const SchemaType extends z.ZodObject | z.ZodPipe<z.ZodObject>,
>({
  view,
  model,
  dataModel,
}: {
  view: ViewType;
  model?: SchemaType;
  dataModel?: DataModelInput;
}): DataModelView<ViewType, SchemaType> => {
  const parsedDataModel = DataModelSchema.parse(dataModel) as DataModelOutput;
  const parsedView = DataModelViewSchema.parse({
    ...view,
    space: view.space ?? parsedDataModel.space,
    version: view.version ?? parsedDataModel.version,
  }) as DataModelView<ViewType, SchemaType>;
  const outputModel = (model ?? z.custom<SchemaType>()) as SchemaType;

  // Collect the set of property names declared on this view, so the
  // `property` proxy below can reject unknown keys instead of silently
  // returning a bogus `[space, table, <unknown>]` tuple that downstream
  // query translators would blindly pass to CDF. Both `ZodObject` (direct
  // `.shape`) and `ZodPipe<ZodObject>` (wrapped, `.out.shape`) are
  // supported. `externalId` and `space` are always valid — they are
  // special-cased below as node-level keys and not part of the schema
  // shape.
  //
  // biome-ignore lint/suspicious/noExplicitAny: Zod internal structure access
  const outputModelAny = outputModel as any;
  const schemaShape = (outputModelAny.shape ?? outputModelAny.out?.shape ?? {}) as Record<string, unknown>;
  const validPropertyNames = new Set<string>(['externalId', 'space', ...Object.keys(schemaShape)]);
  const schema = z.codec(z.custom<NodeOrEdge>(), outputModel, {
    // Cast required: Zod codec encode functions don't automatically narrow the constructed
    // object to NodeOrEdge. The structure is valid but TypeScript can't infer it from the
    // dynamic property keys. Runtime validation ensures correctness.
    encode: (value) =>
      ({
        properties: {
          [parsedView.space]: {
            [parsedView.table]: value,
          },
        },
      }) as unknown as NodeOrEdge,
    decode: ({ space, externalId, createdTime, lastUpdatedTime, properties }): z.input<SchemaType> => ({
      space,
      externalId,
      createdTime: new Date(createdTime),
      lastUpdatedTime: new Date(lastUpdatedTime),
      ...(properties?.[parsedView.space]?.[parsedView.table] as z.input<SchemaType>),
    }),
  });

  return Object.freeze(
    Object.defineProperties(parsedView, {
      dataModel: {
        value: parsedDataModel,
      },
      schema: {
        value: schema,
      },
      table: {
        get() {
          return `${this.externalId}/${this.version}`;
        },
      },
      property: {
        value: new Proxy(
          {},
          {
            get(_target, property) {
              // Let symbol access (Symbol.toPrimitive, Symbol.iterator, etc.)
              // fall through silently — these come from JS engine / debugger
              // introspection and are never legitimate property lookups.
              if (typeof property !== 'string') {
                return undefined;
              }
              if (!validPropertyNames.has(property)) {
                throw new Error(
                  `Property "${property}" does not exist on view "${parsedView.externalId}". ` +
                    `Valid properties: ${[...validPropertyNames].sort().join(', ')}.`,
                );
              }
              return Object.defineProperties(
                ['externalId', 'space'].includes(property)
                  ? (['node', property] as const)
                  : ([parsedView.space, parsedView.table, property] as const),
                {
                  _view: { value: parsedView },
                  _property: { value: property },
                },
              );
            },
          },
        ),
      },
      parseNode: {
        value: (node: NodeOrEdge) => {
          const result = schema.safeParse(node);

          if (!result.success) {
            console.warn('Failed to parse node', node, result);
            throw result.error;
          }

          Object.defineProperty(result.data, '_view', { value: parsedView.externalId });

          return result.data;
        },
      },
    }),
  );
};
